// ── Read-only workflow graph preview ──────────────────────────────────────
//
// Renders a Task[] tree (built by workflowGraphFromSuggestions.ts) through
// the exact same buildGraph()/nodeTypes registry the real Ops Center canvas
// uses, so what a rep sees here — including a conditional BRANCH like
// "Fraud risk detected?" — matches what they'd see if this became a real
// v2 workflow. Read-only: no dragging, connecting, or selecting nodes.
import { useMemo } from 'react'
import { ReactFlow, ReactFlowProvider, Background } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { GitBranch } from 'lucide-react'
import { buildGraph } from '@features/dashboards/components/OpsCenterMockup/graph'
import { nodeTypes } from '@features/dashboards/components/OpsCenterMockup/components/nodes/nodeTypes'
import type { Task } from '@features/dashboards/components/OpsCenterMockup/types'
import { isBranchTask } from '@features/dashboards/components/OpsCenterMockup/types'

interface WorkflowGraphPreviewProps {
  tasks: Task[]
  height?: number
}

function hasBranch(tasks: Task[]): boolean {
  for (const t of tasks) {
    if (isBranchTask(t)) return true
  }
  return false
}

function WorkflowGraphPreviewInner({ tasks, height = 280 }: WorkflowGraphPreviewProps) {
  const { nodes, edges } = useMemo(() => buildGraph(tasks), [tasks])
  const branched = useMemo(() => hasBranch(tasks), [tasks])

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44' }}>
      {branched && (
        <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: 'rgba(234,179,8,.08)', borderBottom: '1px solid #1e2c44' }}>
          <GitBranch size={12} className="text-amber-500 shrink-0" />
          <p className="text-[10px] text-amber-500 font-medium">Conditional branch — steps up to biometrics only when fraud risk is detected</p>
        </div>
      )}
      <div style={{ height, background: '#0a1120' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
        </ReactFlow>
      </div>
    </div>
  )
}

/** Wraps in its own ReactFlowProvider so it can be dropped anywhere without
 * the caller needing to know it's a React Flow canvas underneath. */
export function WorkflowGraphPreview(props: WorkflowGraphPreviewProps) {
  return (
    <ReactFlowProvider>
      <WorkflowGraphPreviewInner {...props} />
    </ReactFlowProvider>
  )
}
